import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Grid2, Typography } from "@mui/material";
import AppButton from "../../components/CommonReusableComponent/AppButton/AppButton";
import { getPageTitle, setRequiredDataForDropdown } from "../../common/CommonFunctions";
import { getListOfAuditType, getListOfAuditTypeCategories } from "../../helpers/api-functions-helper";
import { useSnackbar } from "../../contexts/Snackbar.context";
import { SNACKBAR_VARIANT, ERROR_MESSAGE } from "../../config/Messages.config";
import MUILoader from "../../components/MUILoader/MUILoader";
import AuditTypeMasterDataTable from "./components/AuditTypeMasterDataTable";
import { auditTypeMasterColumnData } from "./components/AuditTypeMaster.config";
import { handleOutSideDataTableSearch } from "../../common/MuiTableFunctions.config";
import MUITableSearch from "../../components/MUITable/MUITableSearch";
import Divider from "@mui/material/Divider";
import { getUserRoleAndScreens } from "../../services/authServices/auth.services";
import { Icons } from "../../config/MuiIcons.config";
import { AppTheme } from "../../theme/App.theme";
import AuditTypeBulkUploadModal from "./components/AuditTypeBulkUploadModal";
import AuditTypeMasterDataTable_V2 from "./components/AuditTypeMasterDataTable_V2";
import AppAutoCompleteField from "../../components/CommonReusableComponent/AppAutoCompleteField/AppAutoCompleteField";

const AuditTypeMaster = () => {
  const documentTitle = getPageTitle() && getPageTitle();
  document.title = documentTitle
    ? `${documentTitle} | Zing Auditor`
    : "Zing Auditor";
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { accountId } = getUserRoleAndScreens();
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [tableDataInstance, setTableDataInstance] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [auditTypeCategories, setAuditTypeCategories] = useState([]);

  // search functionality
  const [searchText, setSearchText] = useState("");

  // states related to checklist bulk upload
  const [toggleActivityBulkUploadState, setToggleActivityBulkUploadState] =
    useState(false);

  // Filters related to audit master
  const [filters, setFilters] = useState({
    auditTypeCategoryId: "All",
  });

  // loading state
  const [loadingState, setLoadingState] = useState({
    isAuditTypesFetched: false,
    isCategoriesFetched: false,
  });

  // All Object for selection
  const allObjWithNameId = [{ name: "All", id: "All" }];

  // function to toggle checklist bulk upload modal
  const toggleActivityBulkUploadModal = () => {
    setToggleActivityBulkUploadState((e) => !e);
  };

  // Function to update loading state
  const setLoading = (key, value) => {
    setLoadingState((prev) => {
      return { ...prev, [key]: value };
    });
  };

  const { auditTypeCategoryId } = filters;

  // Function used to filter data
  const getFilteredData = (filters, searchText, Data) => {
    const searchFilteredData = searchText
      ? handleOutSideDataTableSearch(searchText, Data)
      : tableDataInstance;

    return searchFilteredData.filter((item) => {
      return Object.keys(filters).every((key) => {
        if (filters[key] === "All") {
          return true; // Ignore this filter and include all values
        }

        if (!item.hasOwnProperty(key)) {
          return false; // If key doesn't exist, exclude the item
        }

        return (
          String(item[key]).toLowerCase() === String(filters[key]).toLowerCase()
        );
      });
    });
  };

  const fetchAllAuditTypeCategories = async () => {
    setLoading("isCategoriesFetched", true);
    try {
      const categoryRes = await getListOfAuditTypeCategories(accountId);
      const { statusCode, data, message } = categoryRes?.data;
      if (statusCode && statusCode === 200) {
        let dataToBeSet = [...allObjWithNameId, ...data];
        setRequiredDataForDropdown(
          setAuditTypeCategories,
          dataToBeSet,
          "name",
          "id"
        );
      } else {
        showSnackbar(
          data || message || ERROR_MESSAGE.FAILED_TO_FETCH_DATA,
          SNACKBAR_VARIANT.ERROR
        );
      }
    } catch (error) {
      console.log("Error: ", error);
      showSnackbar(ERROR_MESSAGE.FAILED_TO_FETCH_DATA, SNACKBAR_VARIANT.ERROR);
    } finally {
      setLoading("isCategoriesFetched", false);
    }
  };

  // Fetch all work permit type
  const fetchAllAuditTypes = async () => {
    setLoading("isAuditTypesFetched", true);
    try {
      const response = await getListOfAuditType(accountId);
      if (response && response?.data) {
        const { statusCode, data, message, success } = response.data;
        if (success && statusCode === 200) {
          const modifiedData =
            data && data?.length
              ? data.map((au_type) => {
                  return {
                    ...au_type,
                    auditTypeCategoryName:
                      au_type?.auditTypeCategoryName || "Not Available",
                  };
                })
              : [];
          setTableData(modifiedData);
          setTableDataInstance(modifiedData);
        } else {
          showSnackbar(
            data || message || ERROR_MESSAGE.FAILED_TO_FETCH_DATA,
            SNACKBAR_VARIANT.ERROR
          );
          setTableData([]);
          setTableDataInstance([]);
        }
      }
    } catch (error) {
      console.log("Error: ", error);
      showSnackbar(ERROR_MESSAGE.FAILED_TO_FETCH_DATA, SNACKBAR_VARIANT.ERROR);
    } finally {
      setLoading("isAuditTypesFetched", false);
    }
  };

  const getRowDataOnClickOfAction = (ActionName, Data, NavigationPath) => {
    if (ActionName === "Update Audit Type") {
      localStorage.setItem(
        process.env.REACT_APP_DEV_AUDIT_TYPE_DETAILS,
        JSON.stringify(Data)
      );
      navigate(`${NavigationPath}/${Data.id}`);
    } else {
      localStorage.setItem(
        process.env.REACT_APP_DEV_AUDIT_TYPE_DETAILS,
        JSON.stringify(Data)
      );
      navigate(`${NavigationPath}/${Data.id}`);
    }
  };

  useEffect(() => {
    if (navigator.onLine) {
      fetchAllAuditTypes();
      fetchAllAuditTypeCategories();
    } else {
      showSnackbar(ERROR_MESSAGE.INTERNET_ERROR, SNACKBAR_VARIANT.ERROR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Outside Table search functionality
  useEffect(() => {
    if (searchText === "") {
      const filteredData_ = getFilteredData(
        { ...filters },
        searchText,
        tableDataInstance
      );
      // Reset to original data if search text is cleared
      setTableData(filteredData_);
    } else {
      const filteredData_ = getFilteredData(
        { ...filters },
        searchText,
        tableDataInstance
      );
      setTableData(filteredData_);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // Handle global loading state
  useEffect(() => {
    // Global loading state
    const isAllLoadingComplete = Object.values(loadingState).every(
      (status) => !status
    );

    if (!isAllLoadingComplete) {
      setIsPageLoading(true)
    } else {
      setIsPageLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingState]);

  // Function to change filter
  const handleChangeFilter = (Data, FieldName) => {
    setFilters((prev) => ({
      ...prev,
      [FieldName]: Data?.value || null,
    }));

    const filtered_data = getFilteredData(
      { ...filters, [FieldName]: Data?.value || null },
      searchText,
      tableDataInstance
    );
    // setTableDataInstance(filtered_data);
    setTableData(filtered_data);
  };

  return (
    <React.Fragment>
      {toggleActivityBulkUploadState ? (
        <AuditTypeBulkUploadModal
          ModalState={toggleActivityBulkUploadState}
          AuditTypeCategories={[...auditTypeCategories].filter((atc) => atc.label !== "All")}
          ToggleFunction={toggleActivityBulkUploadModal}
          FetchAllAuditTypes={fetchAllAuditTypes}
        />
      ) : null}

      <div className="page-content">
        {isPageLoading ? (
          <MUILoader />
        ) : (
          <div>
            <Grid2
              container
              className="d-flex align-items-center my-1"
              spacing={2}
            >
              <Grid2 size={{ xs: 12, sm: 8, md: 9, lg: 9 }} className="py-1">
                <Typography className="inter-medium-500">
                  {documentTitle}
                </Typography>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 4, md: 3, lg: 3 }} className="py-1">
                <AppButton
                  OnPress={() => {
                    localStorage.removeItem(
                      process.env.REACT_APP_DEV_AUDIT_TYPE_DETAILS
                    );
                    navigate("/audit-type-master/create");
                  }}
                >
                  Create New Audit Type
                </AppButton>
              </Grid2>
            </Grid2>

            <Divider
              style={{ color: "GrayText", border: "0.02rem solid GrayText" }}
              className="mt-2"
            />

            <Grid2 container className="my-2" spacing={2}>
              <Grid2 size={{ xs: 12, sm: 4, md: 3, lg: 3 }}>
                <MUITableSearch
                  CancelSearch={() => {
                    setSearchText("");
                  }}
                  HandleSearch={(e) => setSearchText(e.target.value)}
                  SearchText={searchText}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 4, md: 3, lg: 3 }}>
                <AppAutoCompleteField
                  IsMultiple={false}
                  Options={auditTypeCategories}
                  SortOptions={true}
                  TextInputPlaceholder={"Select audit type category"}
                  TextInputLabel={"Select audit type category"}
                  HandleOnChange={(event, obj) => {
                    handleChangeFilter(obj, "auditTypeCategoryId");
                  }}
                  Value={auditTypeCategoryId}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 4, md: 3, lg: 3 }} className="ms-auto">
                <Button
                  type="button"
                  variant="outlined"
                  fullWidth
                  className="inter-regular-400"
                  endIcon={<Icons.UploadFileIcon />}
                  size="medium"
                  onClick={toggleActivityBulkUploadModal}
                  style={{
                    textTransform: "capitalize",
                    minHeight: "39px",
                    color: AppTheme.PRIMARY_COLOR,
                    borderColor: AppTheme.PRIMARY_COLOR,
                  }}
                >
                  Audit Type Bulk Upload
                </Button>
              </Grid2>
            </Grid2>

            <Grid2 container className="my-2">
              <Grid2 size={12}>
                {/* <AuditTypeMasterDataTable
                  Columns={auditTypeMasterColumnData}
                  RowData={tableData || []}
                  DenseTable={true}
                  GetRowDataOnClickOfEditIcon={(
                    actionName,
                    data,
                    navigationPath
                  ) => {
                    getRowDataOnClickOfAction(actionName, data, navigationPath);
                  }}
                  RowsToDisplay={10}
                  OptionsForRowsPerPage={[5, 10, 15, 25, 50]}
                  IsOutSideSearch={true}
                /> */}
                <AuditTypeMasterDataTable_V2
                  Rows={tableData || []}
                  GetRowDataOnClickOfAction={getRowDataOnClickOfAction}
                />
              </Grid2>
            </Grid2>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default AuditTypeMaster;
